import { getAssetUrl } from '@lighthouse/assets'
import { useCustomViewBlockContext } from '@lighthouse/block'
import { VideoBlock } from '@lighthouse/block/src/blocks'
import type { VideoBlockAbstract } from '@lighthouse/core'
import { getFileTypeByUrl, getImageFullUrlInApplication, isTextValue } from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { useCurrentAppId } from '@/hooks/useApplication'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface VideoBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: VideoBlockAbstract
}

const VideoBlockController: React.FunctionComponent<VideoBlockControllerProps> = ({ blockData, ...rest }) => {
    const {
        config: { videoSource, cover }
    } = blockData
    const appId = useCurrentAppId()

    const { record: viewRecord } = useCustomViewBlockContext()
    const richtextDeps = useMemo(() => [cover, videoSource].filter(Boolean), [cover, videoSource])
    const parseVariable = useParseVariableWithSubscription(richtextDeps)

    const value = useMemo(() => {
        const DEFAULT_SOURCE = getAssetUrl('blocks', 'defaultVideo.mp4')
        if (!videoSource) {
            return DEFAULT_SOURCE
        }

        const cellValue = parseVariable(videoSource, { viewRecord })
        if (!cellValue) {
            return DEFAULT_SOURCE
        }
        if (cellValue.type === 'video' || cellValue.type === 'file') {
            const videoValue =
                cellValue.type === 'file' ? cellValue?.value?.find(v => getFileTypeByUrl(v) === 'video') : cellValue?.value?.[0]
            return cellValue.value ? getImageFullUrlInApplication(appId, videoValue) || DEFAULT_SOURCE : DEFAULT_SOURCE
        }
        const otherValue = cellValue?.value
        if (otherValue && isTextValue(otherValue)) {
            return otherValue
        }
        return DEFAULT_SOURCE
    }, [appId, parseVariable, videoSource, viewRecord])

    const coverImage = useMemo(() => {
        if (!cover) {
            return ''
        }

        const cellValue = parseVariable(cover, { viewRecord })
        if (!cellValue) {
            return ''
        }
        const fileUrl = (cellValue.type === 'file' ? cellValue?.value?.[0] : '') ?? ''
        return getFileTypeByUrl(fileUrl) === 'image' ? fileUrl : ''
    }, [cover, parseVariable, viewRecord])

    return <VideoBlock blockData={blockData} value={value} cover={coverImage} {...rest} />
}

export default VideoBlockController
